/**
 * Duplicate Flight Detection Hook
 *
 * Detects if a flight already exists and provides variant information
 * Backend uses airline-specific patterns:
 * - Per-Season (MH, AA, etc.): Variants reset for each season
 * - Global (OD): Variants continue across all seasons
 */

import { useEffect, useRef, useState } from 'react';
import { useLazyCheckDuplicateFlightQuery } from '../services/redux/api';

interface DuplicateCheckResult {
  isDuplicate: boolean;
  existingVariants: number[];
  maxVariant: number;
  isChecking: boolean;
  nextVariantId: number;
}

interface UseDuplicateFlightDetectionParams {
  flightOwner: string;
  flightNo: string;
  seasonId: string; // Required: backend determines per-season vs global logic
  excludeId?: string; // For edit mode, exclude current flight from check
  enabled?: boolean; // Only check when enabled
}

/**
 * Hook to detect duplicate flights and suggest next variant ID
 * Fills gaps in variant sequence instead of continuing from max
 * Example: [1, 3] → suggests 2 (not 4)
 * Debounced to avoid excessive API calls during typing
 * Backend automatically handles per-season vs global variant logic
 */
export function useDuplicateFlightDetection({
  flightOwner,
  flightNo,
  seasonId,
  excludeId,
  enabled = true,
}: UseDuplicateFlightDetectionParams): DuplicateCheckResult {
  const [triggerCheck, { isLoading, isFetching }] = useLazyCheckDuplicateFlightQuery();
  const [result, setResult] = useState<DuplicateCheckResult>({
    isDuplicate: false,
    existingVariants: [],
    maxVariant: 0,
    isChecking: false,
    nextVariantId: 1,
  });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only check if all required fields are present and enabled
    const hasRequiredFields = flightOwner && flightNo && seasonId;

    if (!enabled || !hasRequiredFields) {
      setResult({
        isDuplicate: false,
        existingVariants: [],
        maxVariant: 0,
        isChecking: false,
        nextVariantId: 1,
      });
      return;
    }

    // Set checking state
    setResult(prev => ({ ...prev, isChecking: true }));

    // Debounce API call (300ms)
    timeoutRef.current = setTimeout(async () => {
      try {
        // Build full flight number: prefix + space + digits (e.g., "AK 8392")
        const fullFlightNo = flightOwner && flightNo
          ? `${flightOwner} ${flightNo.replace(/[^0-9]/g, '')}`
          : flightNo;

        const checkResult = await triggerCheck({
          flightOwner, // Backend may still use this, send it for compatibility
          flightNo: fullFlightNo, // Send full flight number for regex matching
          seasonId, // Required: backend determines per-season vs global
          excludeId,
        }).unwrap();

        setResult({
          isDuplicate: checkResult.isDuplicate,
          existingVariants: checkResult.existingVariants,
          maxVariant: checkResult.maxVariant,
          isChecking: false,
          nextVariantId: checkResult.nextAvailableVariant,
        });
      } catch (error) {
        console.error('Duplicate check failed:', error);
        setResult({
          isDuplicate: false,
          existingVariants: [],
          maxVariant: 0,
          isChecking: false,
          nextVariantId: 1,
        });
      }
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [flightOwner, flightNo, seasonId, excludeId, enabled, triggerCheck]);

  return {
    ...result,
    isChecking: result.isChecking || isLoading || isFetching,
  };
}
